import { RootState, INITIAL_STATE } from "../core/state-shape";
import { http } from "../lib/http";
import { measure } from "@/lib/performance";
import { createProjectScopedStore } from './projectScoped';

export type IndexState = RootState["index"];

type IndexActions = {
  setState: (newState: IndexState) => void;
  patch: (p: Partial<IndexState>) => void;
  reset: () => void;
  start: (paths?: string[], force?: boolean) => Promise<string>;
  poll: (indexId: string) => Promise<void>;
};

export const useIndexStore = createProjectScopedStore<IndexState & IndexActions>('indexing', (set) => ({
  ...INITIAL_STATE.index,
  setState: (newState) => set(newState),
  patch: (p) => set(p),
  reset: () => set({ ...INITIAL_STATE.index }),

  start: measure(async (paths, force) => {
    const res = await http<{ indexId: string; status: string; estimatedTime: number }>(`/api/index/start`, {
      method: "POST",
      body: JSON.stringify({ paths, force }),
    });
    set({ status: "indexing" } as Partial<IndexState>);
    return res.indexId;
  }, 'indexing.start'),

  poll: measure(async (indexId) => {
    const res = await http<{
      indexId: string; status: "indexing" | "complete"; progress: number;
      filesProcessed: number; symbolsExtracted: number; testsDiscovered: number;
    }>(`/api/index/${encodeURIComponent(indexId)}/status`);
    set({
      status: res.status === "complete" ? "ready" : "indexing",
      stats: {
        filesIndexed: res.filesProcessed,
        symbolsExtracted: res.symbolsExtracted,
        testsDiscovered: res.testsDiscovered,
      },
      progress: res.progress,
      lastUpdatedAt: new Date().toISOString(),
    } as Partial<IndexState>);
  }, 'indexing.poll'),
}));